import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { reportApi, clientApi } from '@/services/api'
import type { TrialBalance as TrialBalanceType } from '@/types'
import { formatCurrency } from '@/utils/currency'
import { BookOpenCheck, Download } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

const TYPE_COLORS: Record<string, string> = {
  asset: 'text-blue-600',
  liability: 'text-amber-600',
  equity: 'text-purple-600',
  revenue: 'text-emerald-600',
  expense: 'text-red-600',
}

export default function TrialBalance() {
  const { clientId } = useParams<{ clientId: string }>()
  const [report, setReport] = useState<TrialBalanceType | null>(null)
  const [loading, setLoading] = useState(false)
  const [asAt, setAsAt] = useState(() => new Date().toISOString().split('T')[0])
  const [exporting, setExporting] = useState(false)
  const [currency, setCurrency] = useState('USD')

  useEffect(() => {
    if (clientId) clientApi.get(clientId).then((r) => setCurrency(r.data.client.currency)).catch(() => {})
    generate()
  }, [clientId])

  async function generate() {
    if (!clientId) return
    setLoading(true)
    try {
      const res = await reportApi.trialBalance(clientId, asAt)
      setReport(res.data)
    } finally { setLoading(false) }
  }

  async function handleExport() {
    if (!clientId) return
    setExporting(true)
    try {
      const res = await reportApi.exportPdf(clientId, 'trial-balance', { as_at: asAt })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `trial-balance-${asAt}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Export failed')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Trial Balance</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">All account balances at a point in time</p>
        </div>
        {report && (
          <Button variant="outline" onClick={handleExport} disabled={exporting}>
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label>As at</Label>
              <Input type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)} className="w-auto" />
            </div>
            <Button onClick={generate} disabled={loading}>
              {loading ? 'Generating...' : 'Generate'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="p-5 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </CardContent>
        </Card>
      ) : report ? (
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-muted/30">
            <p className="text-[13px] text-[rgba(0,10,30,0.75)]">As at {report.as_at}</p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-20">Code</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead className="w-24">Type</TableHead>
                  <TableHead className="text-right w-32">Debit</TableHead>
                  <TableHead className="text-right w-32">Credit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.lines.map((line) => (
                  <TableRow key={line.account_id}>
                    <TableCell className="font-mono text-[13px] text-[rgba(0,10,30,0.75)]">{line.account_code}</TableCell>
                    <TableCell className="text-[13px] text-[#000A1E]">{line.account_name}</TableCell>
                    <TableCell>
                      <span className={`text-[13px] font-bold capitalize ${TYPE_COLORS[line.account_type] || 'text-[rgba(0,10,30,0.75)]'}`}>
                        {line.account_type}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-[13px] text-[#000A1E]">
                      {parseFloat(line.debit) > 0 ? formatCurrency(line.debit, currency) : <span className="text-[#CCCED2]">—</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-[13px] text-[#000A1E]">
                      {parseFloat(line.credit) > 0 ? formatCurrency(line.credit, currency) : <span className="text-[#CCCED2]">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <tfoot>
                <tr className="border-t-2 border-border bg-muted/30">
                  <td colSpan={3} className="px-4 py-3 text-[13px] font-bold text-[#000A1E]">Total</td>
                  <td className="px-4 py-3 text-[13px] font-bold text-right tabular-nums text-[#000A1E]">{formatCurrency(report.total_debit, currency)}</td>
                  <td className="px-4 py-3 text-[13px] font-bold text-right tabular-nums text-[#000A1E]">{formatCurrency(report.total_credit, currency)}</td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <BookOpenCheck className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
            <p className="text-[13px] text-[rgba(0,10,30,0.75)]">Select a date and click Generate</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
